import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  Bot,
  Plus,
  Loader2,
  Save,
  Trash2,
  Pencil,
  X,
  Sparkles,
  Briefcase,
  Globe,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const TONE_LABEL = {
  warm: "Acolhedor (próximo, empático)",
  formal: "Formal (corporativo, respeitoso)",
  neutral: "Neutro (educado e direto)",
};

const INTENT_LABELS = {
  interest_confirmed: "✅ Candidato demonstra interesse",
  interest_declined: "🚫 Candidato recusa a vaga",
  interview_accepted: "📅 Candidato aceita a entrevista",
  interview_reschedule: "🔄 Candidato pede para remarcar",
  job_question: "❓ Pergunta sobre a vaga",
  out_of_context: "🤷 Mensagem fora do contexto",
};

const FluxosPage = () => {
  const [flows, setFlows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useDocumentTitle("Fluxos de IA");

  const load = async () => {
    setLoading(true);
    try {
      const [flowsRes, jobsRes, optsRes] = await Promise.all([
        axios.get(`${API}/flows`),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/flows/options`),
      ]);
      setFlows(flowsRes.data || []);
      setJobs(jobsRes.data || []);
      setOptions(optsRes.data);
    } catch (err) {
      toast.error("Erro ao carregar fluxos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    if (!options) return;
    setEditing({
      name: "",
      scope: "job",
      job_id: "",
      tone: "warm",
      greeting: "Oi {nome}! Tudo bem?",
      intents: { ...options.default_intent_instructions },
      persona_instructions: "",
      active: true,
    });
  };

  const startEdit = (flow) => {
    setEditing({ ...flow, intents: { ...flow.intents } });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) {
      toast.error("Dê um nome para o fluxo");
      return;
    }
    if (editing.scope === "job" && !editing.job_id) {
      toast.error("Selecione a vaga");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/flows/${editing.id}`, {
          name: editing.name,
          tone: editing.tone,
          greeting: editing.greeting,
          intents: editing.intents,
          persona_instructions: editing.persona_instructions,
          active: editing.active,
        });
        toast.success("Fluxo atualizado");
      } else {
        await axios.post(`${API}/flows`, editing);
        toast.success("Fluxo criado");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (flow) => {
    if (flow.scope === "global") {
      toast.error("Fluxo padrão não pode ser excluído");
      return;
    }
    if (!window.confirm(`Excluir o fluxo "${flow.name}"?`)) return;
    try {
      await axios.delete(`${API}/flows/${flow.id}`);
      toast.success("Fluxo excluído");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const usedJobIds = useMemo(
    () =>
      new Set(
        flows
          .filter((f) => f.scope === "job" && f.id !== editing?.id)
          .map((f) => f.job_id)
      ),
    [flows, editing?.id]
  );

  const availableJobs = useMemo(
    () => jobs.filter((j) => !usedJobIds.has(j.id)),
    [jobs, usedJobIds]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-violet-600" />
            Fluxos de resposta com IA
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure como a IA conversa com candidatos no WhatsApp. Você sempre
            tem um fluxo "Padrão" e pode criar fluxos específicos por vaga.
          </p>
        </div>
        <Button
          onClick={startNew}
          data-testid="create-flow-button"
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Novo fluxo
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Carregando…
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {flows.map((f) => (
            <li
              key={f.id}
              data-testid={`flow-card-${f.id}`}
              className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-3"
            >
              <div className="flex items-start gap-2">
                <div
                  className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
                    f.scope === "global"
                      ? "bg-blue-50 text-brand-blue"
                      : "bg-violet-50 text-violet-600"
                  }`}
                >
                  {f.scope === "global" ? (
                    <Globe className="w-4 h-4" />
                  ) : (
                    <Briefcase className="w-4 h-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="font-semibold text-sm text-slate-900 truncate">
                      {f.name}
                    </h3>
                    {f.scope === "global" && (
                      <span className="text-[10px] uppercase tracking-wider bg-blue-50 text-brand-blue px-1.5 py-0.5 rounded font-bold">
                        Padrão
                      </span>
                    )}
                    {!f.active && (
                      <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-bold">
                        Inativo
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {TONE_LABEL[f.tone] || f.tone}
                  </p>
                  {f.scope === "job" && (
                    <p className="text-[11px] text-slate-400 truncate mt-0.5">
                      Vaga: {jobs.find((j) => j.id === f.job_id)?.title || "—"}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startEdit(f)}
                  className="flex-1 h-8 text-xs"
                  data-testid={`edit-flow-${f.id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Editar
                </Button>
                {f.scope === "job" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(f)}
                    className="h-8 text-red-600 hover:bg-red-50"
                    data-testid={`delete-flow-${f.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl max-h-[95vh] flex flex-col">
            <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                <h2 className="heading text-lg font-bold">
                  {editing.id ? "Editar fluxo" : "Novo fluxo"}
                </h2>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="text-slate-400 hover:text-slate-700 p-1"
                data-testid="close-editor"
              >
                <X className="w-5 h-5" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Name + Scope + Tone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nome do fluxo</Label>
                  <Input
                    value={editing.name}
                    onChange={(e) =>
                      setEditing({ ...editing, name: e.target.value })
                    }
                    placeholder="Ex: Vagas técnicas - TI"
                    data-testid="flow-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tom de voz</Label>
                  <Select
                    value={editing.tone}
                    onValueChange={(v) =>
                      setEditing({ ...editing, tone: v })
                    }
                  >
                    <SelectTrigger data-testid="flow-tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="warm">{TONE_LABEL.warm}</SelectItem>
                      <SelectItem value="formal">{TONE_LABEL.formal}</SelectItem>
                      <SelectItem value="neutral">{TONE_LABEL.neutral}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Scope (only when creating) */}
              {!editing.id && (
                <div>
                  <Label className="text-xs">Escopo</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({ ...editing, scope: "job", job_id: "" })
                      }
                      data-testid="scope-job-btn"
                      className={`flex-1 px-3 py-2 rounded-md border text-xs font-semibold ${
                        editing.scope === "job"
                          ? "bg-violet-50 border-violet-300 text-violet-700"
                          : "bg-white border-slate-200 text-slate-500"
                      }`}
                    >
                      <Briefcase className="w-3.5 h-3.5 inline mr-1" />
                      Para uma vaga específica
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    O fluxo "Padrão" (global) já existe e responde quando uma
                    vaga não tem fluxo próprio.
                  </p>
                </div>
              )}

              {/* Job selector */}
              {editing.scope === "job" && !editing.id && (
                <div>
                  <Label className="text-xs">Vaga vinculada</Label>
                  <Select
                    value={editing.job_id}
                    onValueChange={(v) => setEditing({ ...editing, job_id: v })}
                  >
                    <SelectTrigger data-testid="flow-job-select">
                      <SelectValue placeholder="Selecione a vaga…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableJobs.length === 0 ? (
                        <div className="text-xs text-slate-500 px-3 py-2">
                          Todas as vagas já têm fluxo
                        </div>
                      ) : (
                        availableJobs.map((j) => (
                          <SelectItem value={j.id} key={j.id}>
                            {j.title}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Greeting */}
              <div>
                <Label className="text-xs">
                  Saudação inicial (opcional, usado na primeira mensagem)
                </Label>
                <Input
                  value={editing.greeting}
                  onChange={(e) =>
                    setEditing({ ...editing, greeting: e.target.value })
                  }
                  placeholder="Oi {nome}! Tudo bem?"
                  data-testid="flow-greeting-input"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Use <code>{"{nome}"}</code> para o primeiro nome do candidato.
                </p>
              </div>

              {/* Intents */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  Como a IA reage a cada situação
                </h3>
                {Object.entries(INTENT_LABELS).map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-xs">{label}</Label>
                    <Textarea
                      value={editing.intents[key] || ""}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          intents: { ...editing.intents, [key]: e.target.value },
                        })
                      }
                      rows={2}
                      placeholder="Diga em linguagem natural como a IA deve reagir…"
                      data-testid={`flow-intent-${key}`}
                      className="text-xs"
                    />
                  </div>
                ))}
              </div>

              {/* Persona extra */}
              <div>
                <Label className="text-xs">
                  Instruções extras da empresa (opcional)
                </Label>
                <Textarea
                  value={editing.persona_instructions}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      persona_instructions: e.target.value,
                    })
                  }
                  rows={3}
                  placeholder="Ex: Sempre mencione que somos certificados ISO 9001. Se candidato perguntar sobre vale-refeição, informe que oferecemos R$ 35/dia…"
                  data-testid="flow-persona-input"
                  className="text-xs"
                />
              </div>

              {/* Active toggle */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) =>
                    setEditing({ ...editing, active: e.target.checked })
                  }
                  data-testid="flow-active-checkbox"
                />
                Fluxo ativo (a IA usa este fluxo nas conversas)
              </label>

              {/* Tip */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 flex gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  A IA só responde automaticamente quando o RH ativar o botão{" "}
                  <strong>"Ativar IA"</strong> dentro de uma conversa específica.
                  No menu WhatsApp você também pode usar <strong>"✨ Sugerir"</strong>{" "}
                  para gerar uma resposta da IA sem enviar automaticamente.
                </span>
              </div>
            </div>
            <footer className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2 shrink-0 bg-slate-50">
              <Button variant="outline" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="save-flow-button"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1.5" />
                )}
                Salvar fluxo
              </Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default FluxosPage;
